/*
 * Orvian Calendar — utilidades del portal (sin React).
 *
 * Fechas: el servidor habla en instantes UTC (`2026-10-05T15:00:00Z`) y la interfaz pinta la hora de PARED de
 * una zona IANA (la del calendario principal de la persona, o la que elija el visitante de una página de
 * reserva). La zona del navegador NO se usa para pintar: todo pasa por `Intl.DateTimeFormat` con `timeZone`.
 * Los días se manejan como texto «AAAA-MM-DD» y la aritmética de días se hace en UTC puro, así que el horario
 * de verano no corre nada. Contrato completo: claude/calendario.md.
 */

export const LOCALE = 'es-MX';
export const ZONA_DEFECTO = 'America/Mexico_City';

/** Paleta del backend (`calendario.COLORES`): un color fuera de ella se ignora. */
export const COLORES = [
  { valor: '#2563eb', nombre: 'Azul' },
  { valor: '#0f8a9d', nombre: 'Turquesa' },
  { valor: '#2f9170', nombre: 'Verde' },
  { valor: '#7cb342', nombre: 'Pistache' },
  { valor: '#c4861f', nombre: 'Mostaza' },
  { valor: '#e8710a', nombre: 'Naranja' },
  { valor: '#cf5a67', nombre: 'Coral' },
  { valor: '#b8577f', nombre: 'Frambuesa' },
  { valor: '#9c6ac8', nombre: 'Lavanda' },
  { valor: '#5c6bc0', nombre: 'Índigo' },
  { valor: '#6d6875', nombre: 'Grafito' },
  { valor: '#455a64', nombre: 'Pizarra' },
];

export const ROLES = [
  { valor: 'libre_ocupado', texto: 'Ver solo libre/ocupado (ocultar detalles)' },
  { valor: 'lector', texto: 'Ver todos los detalles de los eventos' },
  { valor: 'editor', texto: 'Hacer cambios en los eventos' },
  { valor: 'admin', texto: 'Hacer cambios y administrar el uso compartido' },
];
export const ROL_CORTO = { libre_ocupado: 'Libre/ocupado', lector: 'Ver detalles', editor: 'Hacer cambios', admin: 'Administrar' };

const pad = (n) => String(n).padStart(2, '0');

/* ------------------------------------------------------------------ zonas */
export function zonaNavegador() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ZONA_DEFECTO;
  } catch {
    return ZONA_DEFECTO;
  }
}

const ZONAS_BASICAS = [
  'America/Mexico_City', 'America/Cancun', 'America/Merida', 'America/Monterrey', 'America/Chihuahua',
  'America/Hermosillo', 'America/Mazatlan', 'America/Tijuana', 'America/Bogota', 'America/Lima',
  'America/Santiago', 'America/Argentina/Buenos_Aires', 'America/Sao_Paulo', 'America/Caracas',
  'America/Guatemala', 'America/Costa_Rica', 'America/Panama', 'America/New_York', 'America/Chicago',
  'America/Denver', 'America/Los_Angeles', 'America/Phoenix', 'Europe/Madrid', 'Europe/London',
  'Europe/Paris', 'Europe/Berlin', 'UTC', 'Asia/Tokyo', 'Asia/Shanghai', 'Australia/Sydney',
];

let _zonas = null;
/** Todas las zonas IANA que conoce el navegador (o una lista corta si es viejo). */
export function listaZonas() {
  if (_zonas) return _zonas;
  let lista = [];
  try {
    if (typeof Intl.supportedValuesOf === 'function') lista = Intl.supportedValuesOf('timeZone');
  } catch {
    lista = [];
  }
  if (!lista.length) lista = ZONAS_BASICAS;
  _zonas = Array.from(new Set([...lista, ZONA_DEFECTO, 'UTC'])).sort();
  return _zonas;
}

/** «(GMT-06:00) Ciudad de México» para un selector. */
export function nombreZona(tz, cuando = new Date()) {
  try {
    const off = desfaseMin(cuando.getTime(), tz);
    const signo = off >= 0 ? '+' : '-';
    const a = Math.abs(off);
    const ciudad = (tz.split('/').pop() || tz).replace(/_/g, ' ');
    return `(GMT${signo}${pad(Math.floor(a / 60))}:${pad(a % 60)}) ${ciudad}`;
  } catch {
    return tz;
  }
}

/* --------------------------------------------------- instante ↔ hora de pared */
const _fmts = new Map();
function formateador(tz, opciones, locale = 'en-US') {
  const clave = `${locale}|${tz}|${JSON.stringify(opciones)}`;
  let f = _fmts.get(clave);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat(locale, { ...opciones, timeZone: tz });
    } catch {
      f = new Intl.DateTimeFormat(locale, { ...opciones, timeZone: ZONA_DEFECTO });
    }
    _fmts.set(clave, f);
  }
  return f;
}

/** Partes de la hora de pared de un instante en una zona: {y, m, d, h, mi}. */
export function partes(instante, tz) {
  const d = instante instanceof Date ? instante : new Date(instante);
  const f = formateador(tz, {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
    hourCycle: 'h23', numberingSystem: 'latn',
  });
  const p = {};
  for (const x of f.formatToParts(d)) p[x.type] = x.value;
  let h = Number(p.hour);
  if (h === 24) h = 0;
  return { y: Number(p.year), m: Number(p.month), d: Number(p.day), h, mi: Number(p.minute), s: Number(p.second) };
}

/** Minutos que la zona va por delante de UTC en ese instante. */
export function desfaseMin(ms, tz) {
  const p = partes(new Date(ms), tz);
  const pared = Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi, p.s);
  return Math.round((pared - Math.floor(ms / 1000) * 1000) / 60000);
}

/** Hora de pared («AAAA-MM-DD» + minutos desde medianoche) en una zona → instante (Date). */
export function deLocal(ymd, minutos, tz) {
  const [y, m, d] = ymd.split('-').map(Number);
  const pared = Date.UTC(y, m - 1, d, 0, 0) + minutos * 60000;
  let t = pared - desfaseMin(pared, tz) * 60000;
  t = pared - desfaseMin(t, tz) * 60000;
  return new Date(t);
}

export const ymdDe = (instante, tz) => {
  const p = partes(instante, tz);
  return `${p.y}-${pad(p.m)}-${pad(p.d)}`;
};
export const minutosDe = (instante, tz) => {
  const p = partes(instante, tz);
  return p.h * 60 + p.mi;
};
export const hoyEn = (tz) => ymdDe(new Date(), tz);

/** Instante → ISO UTC con Z y sin milisegundos (lo que espera el backend). */
export const isoUtc = (fecha) => new Date(fecha).toISOString().replace(/\.\d{3}Z$/, 'Z');

/* ------------------------------------------------------------- días «AAAA-MM-DD» */
const aUtc = (ymd) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};
const deUtc = (dt) => `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;

export const sumarDias = (ymd, n) => {
  const dt = aUtc(ymd);
  dt.setUTCDate(dt.getUTCDate() + n);
  return deUtc(dt);
};
export const diffDias = (a, b) => Math.round((aUtc(b) - aUtc(a)) / 86400000);
/** 0 = lunes … 6 = domingo */
export const diaSemana = (ymd) => (aUtc(ymd).getUTCDay() + 6) % 7;
export const inicioSemana = (ymd) => sumarDias(ymd, -diaSemana(ymd));
export const inicioMes = (ymd) => `${ymd.slice(0, 7)}-01`;
export function sumarMeses(ymd, n) {
  const [y, m, d] = ymd.split('-').map(Number);
  const base = new Date(Date.UTC(y, m - 1 + n, 1));
  const ultimo = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + 1, 0)).getUTCDate();
  base.setUTCDate(Math.min(d, ultimo));
  return deUtc(base);
}
export const diaDelMes = (ymd) => Number(ymd.slice(8, 10));
export const mismoMes = (a, b) => a.slice(0, 7) === b.slice(0, 7);

/** Las 6 semanas (42 días) que pinta un mes, empezando en lunes. */
export function rejillaMes(ymd) {
  const primero = inicioSemana(inicioMes(ymd));
  return Array.from({ length: 42 }, (_, i) => sumarDias(primero, i));
}

/** Texto de un día (formato de Intl en español) sin pasar por la zona del navegador. */
export function textoDia(ymd, opciones) {
  return formateador('UTC', opciones, LOCALE).format(aUtc(ymd));
}
export const nombreDiaCorto = (ymd) => textoDia(ymd, { weekday: 'short' }).replace('.', '');
export const nombreMes = (ymd) => textoDia(ymd, { month: 'long', year: 'numeric' });
export const textoDiaLargo = (ymd) => textoDia(ymd, { weekday: 'long', day: 'numeric', month: 'long' });

export const DIAS_CORTOS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
export const DIAS_NOMBRE = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];
export const DIAS_CLAVE = ['lun', 'mar', 'mie', 'jue', 'vie', 'sab', 'dom'];

/* ------------------------------------------------------------------- horas */
/* Las horas se ENSEÑAN a 12 horas («9:30 a. m.», pedido del usuario 2026-10-06); `hhmm` sigue siendo el valor
   interno «HH:MM» de 24 h de los formularios y de la API. */
export const sufijo12 = (h) => (h % 24 < 12 ? 'a. m.' : 'p. m.');
export const h12 = (h) => ((h % 12) || 12);
export function fmtHora(instante, tz) {
  const p = partes(instante, tz);
  return `${h12(p.h)}:${pad(p.mi)} ${sufijo12(p.h)}`;
}
export const hhmm = (min) => `${pad(Math.floor(min / 60) % 24)}:${pad(min % 60)}`;
/** Minutos del día → «9:30 a. m.» */
export const hora12 = (min) => {
  const m = ((min % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60);
  return `${h12(h)}:${pad(m % 60)} ${sufijo12(h)}`;
};
/** «HH:MM» → «9:30 a. m.» */
export const hora12DeHhmm = (t) => hora12(deHhmm(t));
export const deHhmm = (t) => {
  const [h, m] = String(t || '0:0').split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};
/** Etiqueta de la hora en la columna de la rejilla: «9 a. m.». */
export const etiquetaHora = (h) => `${h12(h)} ${sufijo12(h)}`;

/** «lunes, 5 de octubre · 9:00 – 10:00» de un evento, en la zona dada. */
export function textoCuando(ev, tz) {
  if (!ev) return '';
  if (ev.todo_el_dia) {
    const ini = ev.fecha_inicio;
    const ult = sumarDias(ev.fecha_fin || sumarDias(ini, 1), -1);
    if (ult === ini) return `${capital(textoDiaLargo(ini))} · Todo el día`;
    return `${capital(textoDia(ini, { day: 'numeric', month: 'long' }))} – ${textoDia(ult, { day: 'numeric', month: 'long', year: 'numeric' })}`;
  }
  const d0 = ymdDe(ev.inicio, tz);
  const d1 = ymdDe(new Date(new Date(ev.fin).getTime() - 1), tz);
  if (d0 === d1) return `${capital(textoDiaLargo(d0))} · ${fmtHora(ev.inicio, tz)} – ${fmtHora(ev.fin, tz)}`;
  return `${capital(textoDia(d0, { day: 'numeric', month: 'long' }))}, ${fmtHora(ev.inicio, tz)} – ${textoDia(d1, { day: 'numeric', month: 'long' })}, ${fmtHora(ev.fin, tz)}`;
}

export const capital = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/* --------------------------------------------------------- recordatorios */
export function textoRecordatorio(min) {
  if (min === 0) return 'A la hora del evento';
  if (min % 10080 === 0) return `${min / 10080} ${min === 10080 ? 'semana' : 'semanas'} antes`;
  if (min % 1440 === 0) return `${min / 1440} ${min === 1440 ? 'día' : 'días'} antes`;
  if (min % 60 === 0) return `${min / 60} ${min === 60 ? 'hora' : 'horas'} antes`;
  return `${min} minutos antes`;
}
/** minutos → {valor, unidad} para el editor */
export function partirRecordatorio(min) {
  if (min && min % 10080 === 0) return { valor: min / 10080, unidad: 'semanas' };
  if (min && min % 1440 === 0) return { valor: min / 1440, unidad: 'dias' };
  if (min && min % 60 === 0) return { valor: min / 60, unidad: 'horas' };
  return { valor: min, unidad: 'minutos' };
}
export const UNIDADES = { minutos: 1, horas: 60, dias: 1440, semanas: 10080 };

/* --------------------------------------------------------------- repetición */
export const BYDAY = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

export function leerRrule(regla) {
  const r = { freq: 'WEEKLY', interval: 1, byday: [], fin: 'nunca', count: 10, until: '' };
  if (!regla) return r;
  for (const parte of String(regla).replace(/^RRULE:/i, '').split(';')) {
    const [k, v] = parte.split('=');
    if (k === 'FREQ') r.freq = v;
    else if (k === 'INTERVAL') r.interval = Math.max(1, Number(v) || 1);
    else if (k === 'BYDAY') r.byday = v.split(',').map((x) => x.slice(-2)).filter((x) => BYDAY.includes(x));
    else if (k === 'COUNT') { r.fin = 'veces'; r.count = Number(v) || 1; }
    else if (k === 'UNTIL') {
      r.fin = 'fecha';
      r.until = `${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6, 8)}`;
    }
  }
  return r;
}

export function construirRrule({ freq, interval, byday, fin, count, until }) {
  const p = [`FREQ=${freq}`];
  if (interval > 1) p.push(`INTERVAL=${interval}`);
  if (freq === 'WEEKLY' && byday?.length) p.push(`BYDAY=${BYDAY.filter((d) => byday.includes(d)).join(',')}`);
  if (fin === 'veces') p.push(`COUNT=${Math.max(1, Number(count) || 1)}`);
  else if (fin === 'fecha' && until) p.push(`UNTIL=${until.replace(/-/g, '')}`);
  return p.join(';');
}

/** Opciones rápidas de repetición para un día de inicio (como Google). */
export function presetsRepeticion(ymd) {
  const wd = diaSemana(ymd);
  const d = diaDelMes(ymd);
  return [
    { id: '', texto: 'No se repite', rrule: null },
    { id: 'diaria', texto: 'Cada día', rrule: 'FREQ=DAILY' },
    { id: 'semanal', texto: `Cada semana el ${DIAS_NOMBRE[wd]}`, rrule: `FREQ=WEEKLY;BYDAY=${BYDAY[wd]}` },
    { id: 'mensual', texto: `Cada mes el día ${d}`, rrule: 'FREQ=MONTHLY' },
    { id: 'anual', texto: `Cada año el ${textoDia(ymd, { day: 'numeric', month: 'long' })}`, rrule: 'FREQ=YEARLY' },
    { id: 'laborables', texto: 'Todos los días laborables (lunes a viernes)', rrule: 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR' },
  ];
}

/** ¿Qué preset es esta regla para ese día? (null = personalizada) */
export function presetDe(regla, ymd) {
  if (!regla) return '';
  const limpia = String(regla).replace(/^RRULE:/i, '').toUpperCase();
  const p = presetsRepeticion(ymd).find((x) => x.rrule && x.rrule === limpia);
  return p ? p.id : null;
}

/** Texto en español de una regla (cuando el servidor no lo trae, p. ej. mientras se edita). */
export function describirRrule(regla) {
  if (!regla) return 'No se repite';
  const r = leerRrule(regla);
  const n = r.interval;
  const base = {
    DAILY: n === 1 ? 'Cada día' : `Cada ${n} días`,
    WEEKLY: n === 1 ? 'Cada semana' : `Cada ${n} semanas`,
    MONTHLY: n === 1 ? 'Cada mes' : `Cada ${n} meses`,
    YEARLY: n === 1 ? 'Cada año' : `Cada ${n} años`,
  }[r.freq] || 'Se repite';
  let texto = base;
  if (r.freq === 'WEEKLY' && r.byday.length) {
    const dias = BYDAY.filter((d) => r.byday.includes(d)).map((d) => DIAS_NOMBRE[BYDAY.indexOf(d)]);
    texto += ` el ${dias.length > 1 ? `${dias.slice(0, -1).join(', ')} y ${dias[dias.length - 1]}` : dias[0]}`;
  }
  if (r.fin === 'veces') texto += `, ${r.count} veces`;
  else if (r.fin === 'fecha' && r.until) texto += `, hasta el ${textoDia(r.until, { day: 'numeric', month: 'short', year: 'numeric' })}`;
  return texto;
}

/* ---------------------------------------------------------------- eventos */
/** ¿Pertenece a una serie? (pregunta de alcance al editar, mover o borrar) */
export const esRepetido = (ev) => !!(ev && (ev.rrule || ev.serie_id));
/** Clave única de una ocurrencia en la vista. */
export const claveEvento = (ev) => `${ev.id}:${ev.recurrence_id || ''}`;

export const ESTADOS = {
  aceptado: 'Sí',
  rechazado: 'No',
  quizas: 'Quizás',
  pendiente: 'Pendiente',
};

/** ¿Es una URL? (la ubicación puede ser un enlace de videollamada) */
export const esUrl = (t) => /^https?:\/\/\S+$/i.test(String(t || '').trim());

/** Trozos de texto y enlaces de un texto plano (para pintar la descripción sin HTML). */
export function trozosConEnlaces(texto) {
  const salida = [];
  const re = /(https?:\/\/[^\s<>"']+)/gi;
  let ultimo = 0;
  let m;
  const s = String(texto || '');
  while ((m = re.exec(s))) {
    if (m.index > ultimo) salida.push({ texto: s.slice(ultimo, m.index) });
    salida.push({ enlace: m[1] });
    ultimo = m.index + m[1].length;
  }
  if (ultimo < s.length) salida.push({ texto: s.slice(ultimo) });
  return salida;
}

export const CORREO_RE = /^[^@\s<>]+@[^@\s<>]+\.[^@\s<>]+$/;

/** Mensaje legible de un error de axios. */
export function mensajeError(err, defecto = 'Algo salió mal') {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return defecto;
}
